import cactusProfileApi from '../../utils/api/cactusProfile';
import { getStorageItem } from '../../helpers/persistence';

const PERMISSION_LEVEL_TECHNICIAN = 700;

const state = {
  hasAcceptedPolicy: true,
  hasAcceptedTerms: true,
  hasAcceptedNews: true,
  isAuthenticated: false,
  user: null,
  profile: null,
  policyKey: null,
  tcKey: null,
  newsKey: null,
  policyDocument: null,
  termsAndConditionDocument: null,
  newsDocument: null,
  permissionLevel: null
};

const mutations = {
  RESET_STATE: state => {
    const s = initialState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },

  SET_HASACCEPTED_POLICY: (state, hasAcceptedPolicy) => {
    state.hasAcceptedPolicy = hasAcceptedPolicy;
  },
  SET_HASACCEPTED_TERMS: (state, hasAcceptedTerms) => {
    state.hasAcceptedTerms = hasAcceptedTerms;
  },
  SET_HASACCEPTED_NEWS: (state, hasAcceptedNews) => {
    state.hasAcceptedNews = hasAcceptedNews;
  },
  SET_ISAUTHENTICATED: (state, isAuthenticated) => {
    state.isAuthenticated = isAuthenticated;
  },
  SET_USER: (state, user) => {
    state.user = user;
  },
  SET_PROFILE: (state, profile) => {
    state.profile = profile;
  },
  SET_POLICY_KEY: (state, policyKey) => {
    state.policyKey = policyKey;
  },
  SET_TC_KEY: (state, tcKey) => {
    state.tcKey = tcKey;
  },
  SET_NEWS_KEY: (state, newsKey) => {
    state.newsKey = newsKey;
  },
  SET_POLICY_DOCUMENT: (state, policyDocument) => {
    state.policyDocument = policyDocument;
  },
  SET_TERMSANDCONDITION_DOCUMENT: (state, termsAndConditionDocument) => {
    state.termsAndConditionDocument = termsAndConditionDocument;
  },
  SET_NEWS_DOCUMENT: (state, newsDocument) => {
    state.newsDocument = newsDocument;
  },
  SET_PERMISSION_LEVEL: (state, permissionLevel) => {
    state.permissionLevel = permissionLevel;
  },
  SET_ELECTRICITY_PRICE: (state, price) => {
    state.profile.electricity_price = price;
  }
};

const actions = {
  resetState(context) {
    context.commit('RESET_STATE');
  },

  setHasPolicyAccepted(context, status) {
    context.commit('SET_HASACCEPTED_POLICY', status);
  },

  setHasTermAccepted(context, status) {
    context.commit('SET_HASACCEPTED_TERMS', status);
  },

  setHasNewsAccepted(context, status) {
    context.commit('SET_HASACCEPTED_NEWS', status);
  },

  setIsAuthenticated(context, status) {
    context.commit('SET_ISAUTHENTICATED', status);
  },

  setUser(context, payload) {
    context.commit('SET_USER', payload);
  },

  getProfile({ commit }) {
    return cactusProfileApi
      .getProfile()
      .then(response => {
        commit('SET_PROFILE', response.profile || {});
        commit('SET_PERMISSION_LEVEL', response.permission || {});
        return response.profile || undefined;
      })
      .catch(err => {
        commit('SET_PROFILE', {});
        return err;
      });
  },

  getPrivacyPolicyKey({ commit }) {
    return cactusProfileApi
      .getPrivacyPolicyKey()
      .then(response => {
        commit('SET_POLICY_KEY', response);
      })
      .catch(() => {
        commit('SET_POLICY_KEY', []);
        return [];
      });
  },

  getTermsAndConditionsKey({ commit }) {
    return cactusProfileApi
      .getTermsAndConditionsKey()
      .then(response => {
        commit('SET_TC_KEY', response);
      })
      .catch(err => {
        console.error('CactusProfile::getTermsAndConditionsKey()', err);
        commit('SET_TC_KEY', []);
        return [];
      });
  },

  getNewsKey({ commit }) {
    return cactusProfileApi
      .getNewsKey()
      .then(response => {
        commit('SET_NEWS_KEY', response);
      })
      .catch(err => {
        console.error('CactusProfile::getNewsKey()', err);
        commit('SET_NEWS_KEY', []);
        return [];
      });
  },

  getPolicyDocument({ commit }, latestKey) {
    return cactusProfileApi
      .getPolicyDocument(latestKey)
      .then(response => {
        commit('SET_POLICY_DOCUMENT', response);
        commit('SET_HASACCEPTED_POLICY', false);
      })
      .catch(err => {
        console.error('CactusProfile::getPolicyDocument()', err);
        commit('SET_POLICY_DOCUMENT', []);
        return [];
      });
  },

  getTermsAndConditionDocument({ commit }, latestKey) {
    return cactusProfileApi
      .getTermsAndConditionDocument(latestKey)
      .then(response => {
        commit('SET_TERMSANDCONDITION_DOCUMENT', response);
        commit('SET_HASACCEPTED_TERMS', false);
      })
      .catch(() => {
        commit('SET_TERMSANDCONDITION_DOCUMENT', []);
        return [];
      });
  },

  getNewsDocument({ commit }, latestKey) {
    return cactusProfileApi
      .getNewsDocument(latestKey)
      .then(response => {
        commit('SET_NEWS_DOCUMENT', response);
        commit('SET_HASACCEPTED_NEWS', false);
      })
      .catch(() => {
        commit('SET_NEWS_DOCUMENT', []);
        return [];
      });
  },

  updateProfile({ commit, dispatch }, payLoad) {
    return cactusProfileApi
      .updateProfile(payLoad)
      .then(profile => {
        commit('SET_PROFILE', profile);
        dispatch('getProfile');
      })
      .catch(() => {
        commit('SET_PROFILE', []);
        return [];
      });
  },

  getAcceptedPolicy({ commit, state }) {
    return cactusProfileApi
      .getPolicyDocument(state.policyKey.key)
      .then(response => {
        commit('SET_POLICY_DOCUMENT', response);
      })
      .catch(err => {
        console.info('actions::getAcceptedPolicy() - Error: ' + err);
        commit('SET_POLICY_DOCUMENT', []);
        return [];
      });
  },

  getAcceptedTerms({ commit, state }) {
    return cactusProfileApi
      .getTermsAndConditionDocument(state.tcKey.key)
      .then(response => {
        commit('SET_TERMSANDCONDITION_DOCUMENT', response);
      })
      .catch(err => {
        console.info('actions::getAcceptedTerms() - Error: ' + err);
        commit('SET_TERMSANDCONDITION_DOCUMENT', []);
        return [];
      });
  },

  getAcceptedNews({ commit, state }) {
    return cactusProfileApi
      .getNewsDocument(state.newsKey.key)
      .then(response => {
        commit('SET_NEWS_DOCUMENT', response);
      })
      .catch(err => {
        console.info('actions::getAcceptedNews() - Error: ' + err);
        commit('SET_NEWS_DOCUMENT', []);
        return [];
      });
  },

  setElectricityPrice(context, price) {
    context.commit('SET_ELECTRICITY_PRICE', price);
  },

  // Deprecated - Eolectricity price is now saved at `UserProfile`
  loadElectricityPrice({ dispatch }, defaultPrice) {
    defaultPrice = defaultPrice ? defaultPrice : 0.3;
    dispatch('setElectricityPrice', getStorageItem('electricityPrice') || defaultPrice);
  }
};

const getters = {
  hasAcceptedPolicy: state => state.hasAcceptedPolicy,
  hasAcceptedTerms: state => state.hasAcceptedTerms,
  hasAcceptedNews: state => state.hasAcceptedNews,
  isAuthenticated: state => state.isAuthenticated,
  user: state => () => state.user,
  profile: state => state.profile,
  profileCB: state => () => state.profile,
  policyKey: state => state.policyKey,
  tcKey: state => state.tcKey,
  newsKey: state => state.newsKey,
  policyDocument: state => state.policyDocument,
  termsAndConditionDocument: state => state.termsAndConditionDocument,
  newsDocument: state => state.newsDocument,
  isTechnician: state => ((state || {}).permissionLevel || {}).level >= PERMISSION_LEVEL_TECHNICIAN,
  electricityPrice: state => state.profile.electricity_price
};

function initialState() {
  return {
    hasAcceptedPolicy: true,
    hasAcceptedTerms: true,
    hasAcceptedNews: true,
    isAuthenticated: false,
    user: null,
    profile: null,
    policyKey: null,
    tcKey: null,
    newsKey: null,
    policyDocument: null,
    termsAndConditionDocument: null,
    newsDocument: null
  };
}

export default {
  state,
  mutations,
  actions,
  getters
};
